import React, { useState, useEffect } from 'react';
import { landingPathFor } from '../constants/staffNav';

import { useNavigate } from 'react-router-dom';
import axios from 'axios';

/** The cached staff role, for the already-signed-in redirect. */
function roleFromStorage() {
  try {
    const u = JSON.parse(localStorage.getItem('doorly_admin_user') || 'null');
    return (u && u.role) || null;
  } catch { return null; }
}

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function AdminLogin() {
  const navigate = useNavigate();
  // mode = 'login' | 'forgot' | 'forgot-sent'. forgot-sent is the silent
  // success state shown after POST /forgot-password regardless of whether
  // the email was on file (anti-enumeration).
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotSending, setForgotSending] = useState(false);

  // ── 🚨 WHERE A ROLE LANDS, AND /admin/dashboard WAS THE WRONG ANSWER ──────
  //
  // Both redirects in this file sent EVERY role to /admin/dashboard — a page
  // the sidebar hides from Partner Success and whose widgets the server refuses
  // them. So a Partner Success rep began every session on a page their own nav
  // says is not theirs, saw an error box, and reported it as broken
  // permissions. Nothing was broken; the login was pointing them at it.
  //
  // 🔑 landingPathFor() RESOLVES THE FIRST PAGE THEIR OWN NAV OFFERS, derived
  // from the nav rather than a per-role list that would be a fourth thing to
  // keep true.
  useEffect(() => {
    const t = localStorage.getItem('doorly_admin_token');
    if (t) navigate(landingPathFor(roleFromStorage()), { replace: true });
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/admin/login`, form);
      localStorage.setItem('doorly_admin_token', res.data.token);
      localStorage.setItem('doorly_admin_user', JSON.stringify(res.data.user));
      if (res.data.forcePasswordChange) {
        localStorage.setItem('doorly_admin_force_pw', '1');
      }
      // 🔑 THE ROLE FROM THE RESPONSE, not from storage — the write above has
      // happened but reading it back to decide a redirect is a round trip
      // through a value we are already holding.
      navigate(landingPathFor(res.data.user && res.data.user.role), { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email) { setError('Email is required.'); return; }
    setForgotSending(true);
    try {
      await axios.post(`${API}/api/admin/forgot-password`, { email: form.email });
      // Backend always returns success — never reveal whether the email
      // is on file. The "check your email" copy stays the same either way.
      setMode('forgot-sent');
    } catch (err) {
      // Network or 500 — surface a generic error so anti-enumeration is
      // preserved even on infrastructure failures.
      setError(err.response?.data?.error || 'Could not send reset email. Please try again.');
    } finally {
      setForgotSending(false);
    }
  };

  const inputCls = 'w-full h-12 px-4 rounded-xl border border-gray-200 text-sm text-gray-900 outline-none focus:ring-2 focus:border-transparent';

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8fafc' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div style={{ borderRadius: '16px', overflow: 'hidden', display: 'inline-block', marginBottom: '16px' }}>
            <img
              src="/doorly_logo_black.png"
              alt="Doorly"
              style={{ height: '36px', width: 'auto', display: 'block' }}
            />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {mode === 'login' ? 'Doorly Desk' : 'Reset your password'}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {mode === 'login'
              ? 'Sign in to the admin dashboard'
              : mode === 'forgot'
                ? "Enter your email and we'll send you a reset link."
                : 'Check your email for a reset link.'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          {error && (
            <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600 flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          )}

          {mode === 'login' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                <input
                  type="email" required autoFocus
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className={inputCls}
                  style={{ '--tw-ring-color': '#3a6ad9' }}
                  placeholder="admin@godoorly.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                <input
                  type="password" required
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  className={inputCls}
                  placeholder="••••••••"
                />
                <div className="mt-2 text-right">
                  <button type="button" onClick={() => { setError(''); setMode('forgot'); }}
                    className="text-xs hover:underline" style={{ color: '#3a6ad9', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    Forgot password?
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 transition-opacity"
                style={{ backgroundColor: '#3a6ad9', opacity: loading ? 0.7 : 1 }}
              >
                {loading ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : null}
                {loading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>
          )}

          {mode === 'forgot' && (
            <form onSubmit={handleForgot} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                <input
                  type="email" required autoFocus
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className={inputCls}
                  style={{ '--tw-ring-color': '#3a6ad9' }}
                  placeholder="admin@godoorly.com"
                />
              </div>
              <button
                type="submit"
                disabled={forgotSending}
                className="w-full h-12 rounded-xl text-sm font-semibold text-white flex items-center justify-center transition-opacity"
                style={{ backgroundColor: '#3a6ad9', opacity: forgotSending ? 0.7 : 1 }}
              >
                {forgotSending ? 'Sending…' : 'Send Reset Link'}
              </button>
              <button type="button" onClick={() => { setError(''); setMode('login'); }}
                className="w-full text-center text-sm hover:underline" style={{ color: '#3a6ad9', background: 'none', border: 'none', cursor: 'pointer' }}>
                Back to login
              </button>
            </form>
          )}

          {mode === 'forgot-sent' && (
            <div className="space-y-4 text-center">
              <p className="text-sm text-gray-700 leading-relaxed">
                If <strong>{form.email}</strong> is on file, we sent a reset link. The link expires in 1 hour.
              </p>
              <p className="text-xs text-gray-500">Didn't get it? Check your spam folder, or try again in a minute.</p>
              <button type="button" onClick={() => { setError(''); setMode('login'); }}
                className="w-full h-12 rounded-xl text-sm font-semibold text-white flex items-center justify-center"
                style={{ backgroundColor: '#3a6ad9' }}>
                Back to login
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">Doorly Desk · Restricted Access</p>
      </div>
    </div>
  );
}
